/**
 * ScenarioForm Component
 * Step wizard form for creating and editing evaluation scenarios
 * Matches the original Alpine.js 3-step implementation
 */

import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import {
  Plus,
  Trash2,
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  Info,
  Lightbulb,
  HelpCircle,
  BookOpen,
  AlertTriangle,
} from 'lucide-react';
import type {
  EvalScenario,
  CreateEvalScenarioRequest,
  UpdateEvalScenarioRequest,
  ConversationTurn,
  EvalCriterion,
  AgentTypeOption,
  JudgerModelOption,
  MetricDefinition,
} from '@/types/evalScenarios';
import { useMetricsForAgent } from '../hooks/useEvalMetrics';

interface ScenarioFormProps {
  scenario?: EvalScenario | null;
  onSubmit: (data: CreateEvalScenarioRequest | UpdateEvalScenarioRequest) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  categories: string[];
  agentTypes: AgentTypeOption[];
  judgerModels: JudgerModelOption[];
}

// Default criteria for new scenarios
const DEFAULT_CRITERIA: EvalCriterion[] = [
  { name: 'hallucination', threshold: 0.3, weight: 1.0, enabled: true },
  { name: 'relevancy', threshold: 0.7, weight: 1.0, enabled: true },
];

/**
 * Check if a model is compatible with DeepEval framework.
 * DeepEval primarily supports OpenAI GPT models.
 * Pattern matching based on known supported models from DeepEval error messages.
 */
const isDeepEvalCompatibleModel = (modelId: string): boolean => {
  if (!modelId) return false;
  const lowerModelId = modelId.toLowerCase();

  // OpenAI GPT models (gpt-3.5, gpt-4, gpt-4o, gpt-4.1, gpt-4.5, gpt-5, etc.)
  if (lowerModelId.startsWith('gpt-')) return true;

  // OpenAI reasoning models (o1, o3, o4, etc.)
  if (/^o[0-9]/.test(lowerModelId)) return true;

  return false;
};

/**
 * Get the preferred default judger model from the list.
 * Preference order: gpt-5-nano > gpt-4o-mini > first available
 */
const getDefaultJudgerModel = (models: JudgerModelOption[]): string => {
  if (!models || models.length === 0) return 'gpt-4o-mini';

  // Preferred models in order
  const preferredModels = ['gpt-5-nano', 'gpt-4o-mini', 'gpt-4o'];

  for (const preferred of preferredModels) {
    const found = models.find((m) => m.value === preferred);
    if (found) return found.value;
  }

  // Fallback to first available model
  return models[0].value;
};

interface FormData {
  name: string;
  description: string;
  agent_type: string;
  conversation: ConversationTurn[];
  expected_response: string;
  criteria: EvalCriterion[];
  min_overall_score: number;
  judger_model_id: string;
  category: string;
  tags: string[];
  priority: number;
  is_active: boolean;
}

export const ScenarioForm: React.FC<ScenarioFormProps> = ({
  scenario,
  onSubmit,
  onCancel,
  isSubmitting = false,
  categories,
  agentTypes,
  judgerModels,
}) => {
  const { messages } = useI18n();
  const t = messages.evalScenarios;
  const tc = messages.common;
  const isEditing = !!scenario;
  const totalSteps = 3;

  // Step wizard state
  const [currentStep, setCurrentStep] = useState(1);

  // Get initial agent type for metrics query
  const initialAgentType = scenario?.agent_type || agentTypes[0]?.value || 'dora';

  // Form data state
  const [formData, setFormData] = useState<FormData>(() => {
    if (scenario) {
      return {
        name: scenario.name,
        description: scenario.description || '',
        agent_type: scenario.agent_type,
        conversation: scenario.conversation || [{ role: 'user', content: '' }],
        expected_response: scenario.expected_response || '',
        criteria: scenario.criteria || DEFAULT_CRITERIA,
        min_overall_score: scenario.min_overall_score || 0.7,
        judger_model_id: scenario.judger_model_id || getDefaultJudgerModel(judgerModels),
        category: scenario.category || '',
        tags: scenario.tags || [],
        priority: scenario.priority || 0,
        is_active: scenario.is_active ?? true,
      };
    }
    return {
      name: '',
      description: '',
      agent_type: agentTypes[0]?.value || 'dora',
      conversation: [{ role: 'user' as const, content: '' }],
      expected_response: '',
      criteria: DEFAULT_CRITERIA,
      min_overall_score: 0.7,
      judger_model_id: getDefaultJudgerModel(judgerModels),
      category: '',
      tags: [],
      priority: 0,
      is_active: true,
    };
  });

  // Fetch available metrics for the selected agent type
  const { data: metricsData } = useMetricsForAgent(formData.agent_type || initialAgentType);
  const availableMetrics = metricsData?.metrics || [];

  // Tag input state
  const [tagInput, setTagInput] = useState('');

  // Metrics help tooltip state
  const [expandedMetricHelp, setExpandedMetricHelp] = useState<string | null>(null);

  // Reset form when scenario changes
  useEffect(() => {
    if (scenario) {
      setFormData({
        name: scenario.name,
        description: scenario.description || '',
        agent_type: scenario.agent_type,
        conversation: scenario.conversation || [{ role: 'user', content: '' }],
        expected_response: scenario.expected_response || '',
        criteria: scenario.criteria || DEFAULT_CRITERIA,
        min_overall_score: scenario.min_overall_score || 0.7,
        judger_model_id: scenario.judger_model_id || getDefaultJudgerModel(judgerModels),
        category: scenario.category || '',
        tags: scenario.tags || [],
        priority: scenario.priority || 0,
        is_active: scenario.is_active ?? true,
      });
    }
    setCurrentStep(1);
  }, [scenario, judgerModels]);

  // Step validation
  const canProceed = (): boolean => {
    switch (currentStep) {
      case 1:
        return formData.name.trim() !== '' && formData.agent_type !== '';
      case 2:
        return (
          formData.conversation.length > 0 &&
          formData.conversation[0].content.trim() !== ''
        );
      case 3:
        return formData.criteria.length > 0 && formData.judger_model_id !== '';
      default:
        return true;
    }
  };

  // Step info
  const getStepTitle = (step: number): string => {
    const titles: Record<number, string> = {
      1: t.step1Title,
      2: t.step2Title,
      3: t.step3Title,
    };
    return titles[step] || '';
  };

  const getStepDescription = (step: number): string => {
    const descriptions: Record<number, string> = {
      1: t.step1Description,
      2: t.step2Description,
      3: t.step3Description,
    };
    return descriptions[step] || '';
  };

  // Navigation
  const nextStep = () => {
    if (canProceed() && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Conversation helpers
  const addConversationTurn = () => {
    const lastRole = formData.conversation.length > 0
      ? formData.conversation[formData.conversation.length - 1].role
      : 'assistant';
    const nextRole: 'user' | 'assistant' = lastRole === 'user' ? 'assistant' : 'user';
    setFormData({
      ...formData,
      conversation: [...formData.conversation, { role: nextRole, content: '' }],
    });
  };

  const removeConversationTurn = (index: number) => {
    if (formData.conversation.length > 1) {
      setFormData({
        ...formData,
        conversation: formData.conversation.filter((_, i) => i !== index),
      });
    }
  };

  const updateConversationTurn = (index: number, field: 'role' | 'content', value: string) => {
    const updated = [...formData.conversation];
    if (field === 'role') {
      updated[index] = { ...updated[index], role: value as 'user' | 'assistant' };
    } else {
      updated[index] = { ...updated[index], content: value };
    }
    setFormData({ ...formData, conversation: updated });
  };

  // Tag helpers
  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
    }
    setTagInput('');
  };

  const removeTag = (index: number) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((_, i) => i !== index),
    });
  };

  // Criteria helpers
  const addCriterion = () => {
    setFormData({
      ...formData,
      criteria: [
        ...formData.criteria,
        { name: '', threshold: 0.7, weight: 1.0, enabled: true },
      ],
    });
  };

  const removeCriterion = (index: number) => {
    setFormData({
      ...formData,
      criteria: formData.criteria.filter((_, i) => i !== index),
    });
  };

  const updateCriterion = (index: number, updates: Partial<EvalCriterion>) => {
    const updated = [...formData.criteria];
    updated[index] = { ...updated[index], ...updates };
    setFormData({ ...formData, criteria: updated });
  };

  const addMetricFromList = (metric: MetricDefinition) => {
    if (formData.criteria.some((c) => c.name === metric.name)) {
      return;
    }
    setFormData({
      ...formData,
      criteria: [
        ...formData.criteria,
        {
          name: metric.name,
          threshold: metric.default_threshold,
          weight: 1.0,
          enabled: true,
        },
      ],
    });
  };

  const isMetricAdded = (metricName: string): boolean => {
    return formData.criteria.some((c) => c.name === metricName);
  };

  // Submit handler
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep < totalSteps) {
      nextStep();
      return;
    }

    const submitData: CreateEvalScenarioRequest | UpdateEvalScenarioRequest = {
      name: formData.name,
      description: formData.description || undefined,
      agent_type: formData.agent_type,
      conversation: formData.conversation,
      expected_response: formData.expected_response || undefined,
      criteria: formData.criteria,
      min_overall_score: formData.min_overall_score,
      judger_model_id: formData.judger_model_id,
      category: formData.category || undefined,
      tags: formData.tags.length > 0 ? formData.tags : undefined,
      priority: formData.priority,
    };

    if (isEditing) {
      (submitData as UpdateEvalScenarioRequest).is_active = formData.is_active;
    }

    onSubmit(submitData);
  };

  return (
    <div className="space-y-6">
      {/* Step Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          {[1, 2, 3].map((step) => (
            <div key={step} className={`flex items-center ${step < 3 ? 'flex-1' : ''}`}>
              {/* Step Circle */}
              <button
                type="button"
                onClick={() => setCurrentStep(step)}
                className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                  currentStep === step
                    ? 'bg-blue-600 text-white'
                    : currentStep > step
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {currentStep > step ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <span>{step}</span>
                )}
              </button>
              {/* Step Label */}
              <div className="ml-3 hidden sm:block">
                <p
                  className={`text-sm font-medium ${
                    currentStep === step ? 'text-blue-600' : 'text-gray-500'
                  }`}
                >
                  {getStepTitle(step)}
                </p>
              </div>
              {/* Connector Line */}
              {step < 3 && (
                <div
                  className={`flex-1 mx-4 h-0.5 ${
                    currentStep > step ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <p className="mt-2 text-sm text-gray-500">{getStepDescription(currentStep)}</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="space-y-4 bg-gray-900 p-6 rounded-lg border border-gray-800">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                {t.scenarioName} <span className="text-red-400">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t.scenarioNamePlaceholder}
                required
                className="bg-gray-800 text-gray-100 border-gray-700"
              />
              <p className="mt-1 text-xs text-gray-400">
                {t.scenarioNameHint}
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                {t.description}
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t.descriptionPlaceholder}
                rows={2}
                className="bg-gray-800 text-gray-100 border-gray-700"
              />
            </div>

            {/* Agent Type */}
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                {t.targetAgent} <span className="text-red-400">*</span>
              </label>
              <Select
                value={formData.agent_type}
                onChange={(e) => setFormData({ ...formData, agent_type: e.target.value })}
                options={agentTypes.map((a) => ({ value: a.value, label: a.label }))}
                className="bg-gray-800 text-gray-100 border-gray-700"
              />
              <p className="mt-1 text-xs text-gray-400">
                {t.targetAgentHint}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">
                  {t.category}
                </label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder={t.categoryPlaceholder}
                  list="category-list"
                  className="bg-gray-800 text-gray-100 border-gray-700"
                />
                <datalist id="category-list">
                  {categories.map((cat) => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-1">
                  {t.priority}
                </label>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })
                  }
                  min={0}
                  max={100}
                  placeholder="0"
                  className="bg-gray-800 text-gray-100 border-gray-700"
                />
                <p className="mt-1 text-xs text-gray-400">{t.priorityHint}</p>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">{t.tags}</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder={t.addTagPlaceholder}
                  className="flex-1 bg-gray-800 text-gray-100 border-gray-700"
                />
                <Button type="button" variant="secondary" onClick={addTag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-900 text-blue-200 rounded text-sm flex items-center gap-1 border border-blue-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(index)}
                      className="hover:text-blue-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Conversation */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-700 flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {t.conversationHint}
              </p>
            </div>

            {/* Conversation Turns */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  {t.testConversation} <span className="text-red-500">*</span>
                </label>
                <Button type="button" variant="secondary" size="sm" onClick={addConversationTurn}>
                  <Plus className="w-4 h-4 mr-1" />
                  {t.addTurn}
                </Button>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {formData.conversation.map((turn, index) => (
                  <div
                    key={index}
                    className={`flex gap-2 p-3 rounded-lg ${
                      turn.role === 'user' ? 'bg-gray-50' : 'bg-green-50'
                    }`}
                  >
                    <div className="flex flex-col gap-2">
                      <select
                        value={turn.role}
                        onChange={(e) => updateConversationTurn(index, 'role', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm w-24"
                      >
                        <option value="user">{t.userRole}</option>
                        <option value="assistant">{t.assistantRole}</option>
                      </select>
                      {formData.conversation.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeConversationTurn(index)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <Textarea
                      value={turn.content}
                      onChange={(e) => updateConversationTurn(index, 'content', e.target.value)}
                      rows={3}
                      className="flex-1"
                      placeholder={
                        turn.role === 'user'
                          ? t.userMessagePlaceholder
                          : t.assistantMessagePlaceholder
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Expected Response */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t.expectedResponse} <span className="text-gray-400">({t.optional})</span>
              </label>

              {/* Detailed Explanation Box */}
              <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-medium text-amber-800 mb-2">
                  💡 {t.expectedResponseDetailTitle}
                </p>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li>{t.expectedResponseDetail1}</li>
                  <li>{t.expectedResponseDetail2}</li>
                  <li>{t.expectedResponseDetail3}</li>
                </ul>
                <p className="text-xs text-amber-600 mt-2 italic">
                  {t.expectedResponseExample}
                </p>
              </div>

              <Textarea
                value={formData.expected_response}
                onChange={(e) => setFormData({ ...formData, expected_response: e.target.value })}
                rows={5}
                placeholder={t.expectedResponsePlaceholder}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t.expectedResponseHint}
              </p>
            </div>
          </div>
        )}

        {/* Step 3: Evaluation Config */}
        {currentStep === 3 && (
          <div className="space-y-4">
            {/* Metrics Guide Panel */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">{t.metricsGuide}</h4>
                  <p className="text-sm text-blue-800">{t.metricsGuideContent}</p>
                </div>
              </div>
            </div>

            {/* LLM Judger Model Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t.llmJudgeModel} <span className="text-red-500">*</span>
              </label>
              <Select
                value={formData.judger_model_id}
                onChange={(e) => setFormData({ ...formData, judger_model_id: e.target.value })}
                options={judgerModels.map((m) => ({ value: m.value, label: m.label }))}
              />
              <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                <Lightbulb className="w-3 h-3 text-yellow-500" />
                {t.llmJudgeModelHint}
              </p>
              {/* Warning for non-DeepEval compatible models */}
              {formData.judger_model_id && !isDeepEvalCompatibleModel(formData.judger_model_id) && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">{t.llmJudgeModelWarning}</p>
                </div>
              )}
            </div>

            {/* Available Metrics Quick Add */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.quickAddMetrics}
              </label>
              <div className="space-y-2">
                {availableMetrics.map((metric) => {
                  const metricKey = metric.name as keyof typeof t.metrics;
                  const helpKey = `${metric.name}Help` as keyof typeof t.metrics;
                  const descKey = `${metric.name}Desc` as keyof typeof t.metrics;
                  const isLowerBetter = metric.threshold_direction === 'lower';

                  return (
                    <div key={metric.name} className="group">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => addMetricFromList(metric)}
                          disabled={isMetricAdded(metric.name)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors flex items-center gap-2 ${
                            isMetricAdded(metric.name)
                              ? 'bg-green-100 border-green-300 text-green-700 cursor-not-allowed'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300'
                          }`}
                        >
                          {isMetricAdded(metric.name) ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <Plus className="w-3 h-3" />
                          )}
                          {t.metrics[metricKey] || metric.display_name}
                        </button>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          isLowerBetter
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {isLowerBetter ? t.lowerIsBetter : t.higherIsBetter}
                        </span>
                        <button
                          type="button"
                          onClick={() => setExpandedMetricHelp(
                            expandedMetricHelp === metric.name ? null : metric.name
                          )}
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                        >
                          <HelpCircle className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-gray-500">
                          {t.metrics[descKey] || metric.description}
                        </span>
                      </div>
                      {/* Expanded help text */}
                      {expandedMetricHelp === metric.name && (
                        <div className="ml-6 mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                          <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <p>{t.metrics[helpKey] || metric.description || metric.name}</p>
                          </div>
                          <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                            {t.threshold}: <strong>{(metric.default_threshold * 100).toFixed(0)}%</strong>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Selected Criteria */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t.evaluationCriteria} <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={addCriterion}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  <Plus className="w-4 h-4 inline mr-1" />
                  {t.customMetric}
                </button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {formData.criteria.map((criterion, index) => {
                  const criterionKey = criterion.name as keyof typeof t.metrics;
                  const matchingMetric = availableMetrics.find((m) => m.name === criterion.name);
                  const isKnownMetric = !!matchingMetric || criterionKey in t.metrics;
                  const displayName = matchingMetric?.display_name || (criterionKey in t.metrics ? t.metrics[criterionKey] : criterion.name);
                  const isLowerBetter = matchingMetric?.threshold_direction === 'lower';

                  return (
                    <div
                      key={index}
                      className="flex gap-3 items-center bg-gray-50 p-3 rounded-lg"
                    >
                      <input
                        type="checkbox"
                        checked={criterion.enabled}
                        onChange={(e) =>
                          updateCriterion(index, { enabled: e.target.checked })
                        }
                        className="rounded border-gray-300 w-5 h-5"
                      />
                      {isKnownMetric ? (
                        <div className="flex-1 flex items-center gap-2">
                          <span className="font-medium text-gray-900">{displayName}</span>
                          {matchingMetric && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              isLowerBetter
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {isLowerBetter ? t.lowerIsBetter : t.higherIsBetter}
                            </span>
                          )}
                          <span className="text-xs text-gray-400">({criterion.name})</span>
                        </div>
                      ) : (
                        <Input
                          value={criterion.name}
                          onChange={(e) => updateCriterion(index, { name: e.target.value })}
                          placeholder={t.metricNamePlaceholder}
                          className="flex-1"
                        />
                      )}
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">{t.threshold}:</span>
                        <Input
                          type="number"
                          value={criterion.threshold}
                          onChange={(e) =>
                            updateCriterion(index, {
                              threshold: parseFloat(e.target.value) || 0,
                            })
                          }
                          min={0}
                          max={1}
                          step={0.05}
                          className="w-20 text-center"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCriterion(index)}
                        className="text-red-500 hover:text-red-700 px-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
                {formData.criteria.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {t.noMetricsSelected}
                  </p>
                )}
              </div>
            </div>

            {/* Min Overall Score */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t.minOverallScore}
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  value={formData.min_overall_score}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      min_overall_score: parseFloat(e.target.value),
                    })
                  }
                  min={0}
                  max={1}
                  step={0.05}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span
                  className={`text-lg font-semibold w-16 text-center ${
                    formData.min_overall_score >= 0.7 ? 'text-green-600' : 'text-yellow-600'
                  }`}
                >
                  {(formData.min_overall_score * 100).toFixed(0)}%
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {t.minOverallScoreHint}
              </p>
            </div>

            {/* Active checkbox (only for editing) */}
            {isEditing && (
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">
                  {t.activeCheckbox}
                </label>
              </div>
            )}
          </div>
        )}

        {/* Modal Footer with Navigation */}
        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={currentStep > 1 ? prevStep : onCancel}
          >
            {currentStep > 1 ? (
              <>
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t.previous}
              </>
            ) : (
              <>
                <X className="w-4 h-4 mr-1" />
                {tc.cancel}
              </>
            )}
          </Button>

          <div className="flex gap-3 items-center">
            <span className="text-sm text-gray-500">
              {t.stepOf.replace('{current}', String(currentStep)).replace('{total}', String(totalSteps))}
            </span>
            <Button
              type="submit"
              variant="primary"
              disabled={!canProceed()}
              loading={currentStep === totalSteps && isSubmitting}
            >
              {currentStep === totalSteps ? (
                <>
                  {isEditing ? t.saveChanges : t.createButton}
                  <Check className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  {t.next}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
